import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, output } from '@angular/core';
import { addDays, endOfDay, startOfDay } from 'date-fns';

import { TerraIconModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';
import { CalendarEvent, CalendarEventsService } from '@ninety/web/pages/shared/integrations';

import { getMeetingPlatform } from '../../../_shared/util/meeting-platform';

/**
 * Embedded content, not a MatDialog - meant to be swapped in place of a host dialog's normal
 * content (see StartMeetingDialogComponent), not opened as its own nested modal. Communicates
 * back to whatever hosts it via `closed`, the same nullable-event contract a MatDialogRef would.
 */
@Component({
  selector: 'ninety-select-calendar-event-dialog',
  standalone: true,
  imports: [CommonModule, TerraIconModule, ButtonComponent],
  templateUrl: './select-calendar-event-dialog.component.html',
  styles: [
    `
      @use 'terra';

      :host {
        display: flex;
        flex-direction: column;
        padding: 24px;
      }
      .select-calendar-event-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        height: 27px;
      }
      .select-calendar-event-subtitle {
        margin: 4px 0 16px;
      }
      .event-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-height: 320px;
        overflow-y: auto;
      }
      .event-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 13px;
        border: 1px solid terra.color('neutral-shader', 'light');
        cursor: pointer;
        @include terra.radius(medium);

        &:hover:not(.disabled):not(.selected) {
          background: terra.color('neutral-light', '300');
          border-color: terra.color('neutral-light', '200');
        }

        &.selected {
          border-color: terra.color('brand');
        }

        &.disabled {
          cursor: default;
          background: terra.color('neutral-shader', 'lightest');

          .event-summary,
          .event-time,
          .text-light {
            color: terra.color('text', 'disabled');
          }
        }
      }
      .event-item-copy {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
      }
      .event-summary {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .event-time {
        flex-shrink: 0;
      }
      .text-light {
        color: terra.color('text', 'light');
      }
      .event-item terra-icon {
        color: terra.color('text', 'lightest');
        flex-shrink: 0;
      }
    `,
  ],
})
export class SelectCalendarEventDialogComponent {
  private static readonly STALE_THRESHOLD_MS = 60 * 60 * 1000;

  readonly calendarService = input.required<CalendarEventsService>();

  readonly connectionId = input.required<string>();
  readonly selectedEventId = input<string | undefined>();

  /** How many days beyond today to include, e.g. 10 to cover the next 10 days. Defaults to today only. */
  readonly daysAhead = input<number>(0);

  /** Human-readable phrase for the date range shown in the loading/empty copy, e.g. "the next 10 days". */
  readonly rangeLabel = input<string>('today');

  /** Subtitle under the title - varies by host dialog (e.g. "today's" vs "upcoming" meetings). */
  readonly subtitle = input<string>("Choose from today's meetings");

  /** Emits the picked event, or undefined when the user cancels/backs out. */
  readonly closed = output<CalendarEvent | undefined>();

  // Recently-ended events still show (disabled, via hasEnded) so the user can see why one they
  // just picked isn't selectable - but anything past the 1-hour grace period is just clutter.
  readonly visibleEvents = computed(() =>
    this.calendarService()
      .events()
      .filter(event => !this.isStale(event))
  );

  getMeetingPlatform = getMeetingPlatform;

  private lastLoadedConnectionId: string | undefined;

  constructor() {
    effect(() => {
      const connectionId = this.connectionId();
      if (!connectionId || connectionId === this.lastLoadedConnectionId) {
        return;
      }
      this.lastLoadedConnectionId = connectionId;
      const now = new Date();
      this.calendarService().getEvents(
        connectionId,
        startOfDay(now).toISOString(),
        endOfDay(addDays(now, this.daysAhead())).toISOString()
      );
    });
  }

  select(event: CalendarEvent): void {
    if (!this.getMeetingPlatform(event.meetingUrl) || this.hasEnded(event)) {
      return;
    }
    this.closed.emit(event);
  }

  // Recall rejects scheduling a bot against a calendar event whose end time has already passed,
  // so keep those unselectable here rather than surfacing that as a bot-scheduling error later.
  hasEnded(event: CalendarEvent): boolean {
    return !!event.end && new Date(event.end) < new Date();
  }

  private isStale(event: CalendarEvent): boolean {
    if (!event.end) {
      return false;
    }
    return Date.now() - new Date(event.end).getTime() > SelectCalendarEventDialogComponent.STALE_THRESHOLD_MS;
  }

  cancel(): void {
    this.closed.emit(undefined);
  }
}
